import * as React from 'react';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { useLocalizer } from './hooks/useLocalizer';
import { AIHeader } from './AIHeader';
import { AIProcessingSummary } from './AIProcessingSummary';
import { AIProcessingStats } from './AIProcessingStats';
import { AIImportedFilesSection } from './AIImportedFilesSection';
import { AIDeleteAllButton } from './AIDeleteAllButton';
import { AIUploadDifferentFilesButton } from './AIUploadDifferentFilesButton';
import { UploadedFile } from './types';
import { DataCollectionAiAgentOperationType } from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels';
import { getActiveAIOperationFromState, shouldShowOfflineGapAnalysisCta } from '../../../store/StateControllers/Survey/SurveyResponse/AISurveyAgentController';

const successIconSrc = '/images/supplier-ai-agent/success-icon.svg';

interface AISuccessStateProps {
    hasAIAnswers: boolean;
    isProcessing: boolean;
    isDeleteCompleted: boolean;
    uploadedFiles: UploadedFile[];
    onError?: (message: string) => void;
    localizer?: IStringLocalizer;
    agentState?: string | null;
    onNavigateToSurvey?: () => void;
    onFilesCleared?: () => void;
    onNextPage?: () => void;
    isReadonly?: boolean;
    operationType: DataCollectionAiAgentOperationType;
}

export const AISuccessState: React.FC<AISuccessStateProps> = ({
    hasAIAnswers,
    isProcessing,
    isDeleteCompleted,
    uploadedFiles,
    onError,
    localizer,
    agentState,
    onNavigateToSurvey,
    onFilesCleared,
    onNextPage,
    isReadonly = false,
    operationType
}) => {
    const localize = useLocalizer(localizer);
    const aiOperation = useSelector((state: ApplicationState) =>
        getActiveAIOperationFromState(state.aiSurveyAgentState, operationType)
    );
    const nextButtonLabel = shouldShowOfflineGapAnalysisCta(operationType, aiOperation)
        ? 'Show Gap Analysis'
        : 'Next';

    const handleNextClick = () => {
        if (onNextPage) {
            onNextPage();
        } else if (onNavigateToSurvey) {
            onNavigateToSurvey();
        }
    };

    if (!hasAIAnswers || isProcessing || isDeleteCompleted) {
        return null;
    }

    return (
        <div>
            <div>
                <AIHeader localizer={localizer} />
            </div>
            <div className="ai-success-container">
                <div className="ai-success-icon-circle">
                    <img src={successIconSrc} width="32" height="32" aria-hidden="true" alt="" />
                </div>

                <h4 className="ai-success-heading">{localize('Analysis Complete')}</h4>

                <AIProcessingSummary agentState={agentState} localizer={localizer} operationType={operationType} />
                <AIProcessingStats localizer={localizer} operationType={operationType} />

                <p className="ai-success-helper-text">
                    {localize('Upload additional relevant documents to improve low confidence and unanswered questions.')}
                </p>

                <div className="ai-success-button-container">
                    <AIUploadDifferentFilesButton
                        onError={onError}
                        localizer={localizer}
                        disabled={isReadonly}
                    />
                    <AIDeleteAllButton
                        onError={onError}
                        localizer={localizer}
                        onFilesCleared={onFilesCleared}
                        disabled={isReadonly}
                        operationType={operationType}
                    />
                </div>
            </div>

            <div className="ai-success-next-container">
                <button
                    type="button"
                    onClick={handleNextClick}
                    className="btn btn-primary-orange ai-next-button"
                >
                    {localize(nextButtonLabel)}
                </button>
            </div>

            <AIImportedFilesSection uploadedFiles={uploadedFiles} localizer={localizer} />
        </div>
    );
};

export default AISuccessState;
